import React, { useEffect, useRef } from "react";
import styles from "./Components.module.scss";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { Label, Error } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { workingDaysData } from "../../../Company/CompanyDetails";

const normalizeWorkingDays = (wd) =>
  Array.isArray(wd)
    ? wd
    : (wd || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

const PersonalDetails = ({ data = {}, onChange, mode, errors = {} }) => {
  const READ_ONLY = mode === "view";
  const selectedDays = React.useMemo(
    () => normalizeWorkingDays(data?.workingDays),
    [data?.workingDays]
  );

  const workingDays = workingDaysData.map((day) => ({
    ...day,
    isChecked: selectedDays.includes(day.label),
  }));

  const handleWorkingDays = (e, day) => {
    const updated = e.value
      ? [...selectedDays, day.label]
      : selectedDays.filter((d) => d !== day.label);

    onChange("workingDays", updated.join(", "));
  };

  const handleFieldChange = (field, value) => {
    if (
      field === "dateJoined" ||
      field === "dateQualified" ||
      field === "dateLeft"
    ) {
      if (!value) {
        value = null;
      } else if (value instanceof Date) {
        value = value.toISOString();
      }
    }
    onChange(field, value);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Personal Details</h2>
      <div className={styles.formGrid}>
        {mode === "add" && (
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <Label>User Ref</Label>
              <Input
                name="userRef"
                value={data.userRef || ""}
                onChange={(e) => onChange("userRef", e.value)}
                placeholder="Enter user reference"
                className={`mt-1 ps-3 ${errors.userRef ? "k-invalid" : ""}`}
                disabled={READ_ONLY}
                style={{ width: 260 }}
              />
              {errors.userRef && <Error>{errors.userRef}</Error>}
            </div>
          </div>
        )}

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>First Name</Label>
            <Input
              name="firstName"
              value={data.firstName || ""}
              onChange={(e) => onChange("firstName", e.value)}
              className={`mt-1 ps-3 ${errors.firstName ? "k-invalid" : ""}`}
              placeholder="Enter first name"
              disabled={READ_ONLY}
            />
            {errors.firstName && <Error>{errors.firstName}</Error>}
          </div>

          <div className={styles.formField}>
            <Label>Last Name</Label>
            <Input
              name="lastName"
              value={data.lastName || ""}
              onChange={(e) => onChange("lastName", e.value)}
              className="mt-1 ps-3"
              placeholder="Enter last name"
              disabled={READ_ONLY}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>Job Title</Label>
            <Input
              name="jobTitle"
              value={data.jobTitle || ""}
              onChange={(e) => onChange("jobTitle", e.value)}
              className="mt-1 ps-3"
              placeholder="Enter job title"
              disabled={READ_ONLY}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>Email Address</Label>
            <Input
              name="emailAddress"
              type="email"
              value={data.emailAddress || ""}
              onChange={(e) => onChange("emailAddress", e.value)}
              className={`mt-1 ps-3 ${errors.emailAddress ? "k-invalid" : ""}`}
              placeholder="Enter email address"
              disabled={READ_ONLY}
            />
            {errors.emailAddress && <Error>{errors.emailAddress}</Error>}
          </div>
          <div className={styles.formField}>
            <Label>Date Joined</Label>
            <DatePicker
              value={data.dateJoined ? new Date(data.dateJoined) : null}
              onChange={(e) => handleFieldChange("dateJoined", e.value)}
              format="dd/MM/yyyy"
              placeholder="Select date"
              disabled={READ_ONLY}
              toggleButton={CalenderCustomToggleButton}
            />
            {errors.dateJoined && <Error>{errors.dateJoined}</Error>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>Phone No</Label>
            <Input
              name="phoneNo"
              type="tel"
              value={data.phoneNo || ""}
              onChange={(e) => onChange("phoneNo", e.value)}
              className="mt-1 ps-3"
              placeholder="Enter phone number"
              disabled={READ_ONLY}
            />
          </div>
          <div className={styles.formField}>
            <Label>Date Qualified</Label>
            <DatePicker
              value={data.dateQualified ? new Date(data.dateQualified) : null}
              onChange={(e) => handleFieldChange("dateQualified", e.value)}
              format="dd/MM/yyyy"
              placeholder="Select date"
              disabled={READ_ONLY}
              toggleButton={CalenderCustomToggleButton}
            />
            {errors.dateQualified && <Error>{errors.dateQualified}</Error>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>Mobile No</Label>
            <Input
              name="mobileNo"
              type="tel"
              value={data.mobileNo || ""}
              onChange={(e) => onChange("mobileNo", e.value)}
              className="mt-1 ps-3"
              placeholder="Enter mobile number"
              disabled={READ_ONLY}
            />
          </div>
          <div className={styles.formField}>
            <Label>Date Left</Label>
            <DatePicker
              value={data.dateLeft ? new Date(data.dateLeft) : null}
              onChange={(e) => handleFieldChange("dateLeft", e.value)}
              format="dd/MM/yyyy"
              placeholder="Select date"
              disabled={READ_ONLY}
              toggleButton={CalenderCustomToggleButton}
            />
            {errors.dateLeft && <Error>{errors.dateLeft}</Error>}
          </div>
        </div>

        <div>
          <Label>Working Days</Label>
          <div className={styles.workingDaysContainer}>
            {workingDays.map((day) => (
              <div
                key={day.key}
                className={`${styles.weekDays} ${
                  !day.isChecked ? styles.opacity : ""
                }`}
              >
                <span className={styles.daysNames}>{day.key}</span>
                <Checkbox
                  checked={day.isChecked}
                  onChange={(e) => handleWorkingDays(e, day)}
                  disabled={READ_ONLY}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
